import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  shiftApi,
  type UpdateShiftSlot,
  type ShiftSlot,
  type ShiftArea,
} from '@/api/shift';

export const useShiftAreas = (enabled: boolean) =>
  useQuery({
    queryKey: ['shift', 'areas'],
    queryFn: shiftApi.getAreas,
    enabled,
  });

export const useShiftSlots = (
  areaId: number | null,
  start: string,
  end: string,
  enabled: boolean
) =>
  useQuery({
    queryKey: ['shift', 'slots', areaId, start, end],
    queryFn: () => shiftApi.getSlots(areaId!, start, end),
    enabled: enabled && areaId !== null,
  });

/**
 * Hook to get user's upcoming shift registrations across all areas
 */
export const useMyUpcomingShifts = (userId: string | undefined) => {
  return useQuery({
    queryKey: ['shift', 'my-upcoming-shifts', userId],
    queryFn: () => shiftApi.getMyUpcomingShifts(),
    enabled: !!userId,
  });
};

function useShiftMutation<TVariables>(
  mutationFn: (variables: TVariables) => Promise<void>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shift'] });
    },
  });
}

export const useRegisterShift = () =>
  useShiftMutation(({ areaId, date }: { areaId: number; date: string }) =>
    shiftApi.register({ areaId, date })
  );

export const useCancelShift = () =>
  useShiftMutation(({ areaId, date }: { areaId: number; date: string }) =>
    shiftApi.cancel({ areaId, date })
  );

export const useUpdateShiftSlot = () =>
  useShiftMutation(
    ({
      areaId,
      date,
      update,
    }: {
      areaId: number;
      date: string;
      update: UpdateShiftSlot;
    }) => shiftApi.updateSlot(areaId, date, update)
  );

export const useAdminDeleteShiftRegistration = () =>
  useShiftMutation(
    ({
      areaId,
      date,
      volunteerId,
    }: {
      areaId: number;
      date: string;
      volunteerId: string;
    }) => shiftApi.adminDeleteRegistration(areaId, date, volunteerId)
  );

export type { ShiftSlot, ShiftArea };
